/* eslint-disable react/no-array-index-key */
import React, {useState, useContext, useEffect, useRef, useCallback} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEdit} from "@fortawesome/free-solid-svg-icons";

// Utils
import useMountedState from "../../hooks/useMountedState.js";

// Components
import ModalEditContacts from "./ModalEditContacts.js";
import Marquee from "../general/Marquee.js";
import PhoneNumbers from "../../components/PhoneNumbers.js";

// Contexts
import {AuthContext} from "../../contexts/auth.js";
import {FacilityNavContext} from "../../contexts/facilitynav.js";

// Style
import {border, pad, radius} from "../../style/components/variables.js";
import {z} from "../../style/components/mixins.js";
import {
  Text,
  Abbr,
  AnchorInline,
  Masonry,
  Inline,
  MasonryItem,
  NotLoaded,
  Loader
} from "../../style/components/general.js";
import voice from "../../style/components/typography.js";

const ContactsPage = ({facility}) => {
  const isMounted = useMountedState();

  const {contactsExtra} = facility.details;

  const {setFacility} = useContext(FacilityNavContext);
  const {roleCanAccessResource} = useContext(AuthContext);

  const [showEditContacts, setShowEditContacts] = useState(false);

  const [contacts, setContacts] = useState(null);
  const [width, setWidth] = useState(3);

  const wrapper = useRef();

  useEffect(() => {
    const temp = {};
    contactsExtra?.map(contact => {
      const formatted = {
        name: `${contact.firstName} ${contact.lastName}`,
        company: contact.company,
        primary: contact.primary,
        phones: contact.phones,
        email: contact.email
      };

      const company = contact.company?.name || "No Company Provided";

      const department = contact?.department?.name || "Contact";
      if (!temp[department]) temp[department] = {[company]: [formatted]};
      else if (!temp[department][company]) temp[department][company] = [formatted];
      else temp[department][company].push(formatted);
    });
    setContacts(temp);
  }, [contactsExtra]);

  const evaluateWidth = useCallback(el => {
    // row length based on screen size
    const rect = (el || wrapper.current)?.getBoundingClientRect();
    if (rect?.width) {
      const rowLength = Math.floor(rect.width / 270);

      if (!Number.isNaN(rowLength)) setWidth(rowLength);
    }
  }, []);

  useEffect(() => {
    if (isMounted()) setWidth();

    window.addEventListener("resize", setWidth);

    return () => window.removeEventListener("resize", setWidth);
  }, [isMounted, setWidth]);

  const packMasonry = (obj, maxColumns) => {
    const rows = [];
    let currentRow = [];
    let columnsFilled = 0;

    const arr = Array.isArray(obj) ? obj : Object.keys(obj);

    for (let i = 0; i < arr.length; i++) {
      const val = arr[i];
      const ct = Array.isArray(obj) ? 1 : obj[val].length;

      // If adding this company would overflow the row, start a new row
      if (columnsFilled + ct > maxColumns || !Array.isArray(obj)) {
        rows.push(currentRow);
        currentRow = [];
        columnsFilled = 0;
      }

      currentRow.push(val);
      columnsFilled += ct;
    }

    // Add last row if not empty
    if (currentRow.length > 0) {
      while (Array.isArray(obj) && columnsFilled < maxColumns) {
        currentRow.push({});
        columnsFilled += 1;
      }
      rows.push(currentRow);
    }
    return rows;
  };

  return (
    <>
      {contacts && Object.keys(contacts).length > 0 ? (
        <ContactsMasonry
          bordered
          ref={val => {
            wrapper.current = val;
            // measure layout size
            evaluateWidth(val);
          }}>
          {roleCanAccessResource("facility_information", "update") && !facility.isDeleted && (
            <Edit type="button" onClick={() => setShowEditContacts(true)}>
              <FontAwesomeIcon icon={faEdit} />
            </Edit>
          )}

          {Object.keys(contacts).map(department => (
            <Detail key={department} fullWidth>
              <DetailLabel fullWidth>
                <Abbr title={department}>{department}</Abbr>
              </DetailLabel>
              <Notes fullWidth>
                {packMasonry(contacts[department], width).map((row, i) => (
                  <Inline key={`companyRow-${i}`} fullWidth wrap nogap>
                    {row.map((company, j) => {
                      const filledSoFar = row
                        .slice(0, j)
                        .reduce((a, c) => a + contacts[department][c].length, 0);

                      const thisCompany = contacts[department][company];

                      const userRows = packMasonry(thisCompany, width - filledSoFar);
                      if (userRows.length === 0) return null;

                      const regionWidth = userRows[0].length;
                      return (
                        <CompanyWrapper key={company} numItems={regionWidth} totalItems={width}>
                          <Marquee text={company} />
                          {userRows.map((userRow, k) => (
                            <CompanyWrapper key={`userRow-${k}`}>
                              <Inline fullWidth nogap wrap>
                                {userRow.map(contact => (
                                  <ContactWrapper
                                    key={contact.email}
                                    numItems={1}
                                    totalItems={regionWidth}>
                                    <NoteText quiet>
                                      <Abbr title={contact.name}>{contact.name}</Abbr>
                                    </NoteText>
                                    {contact.primary &&
                                      contact.phones &&
                                      contact.phones.length > 0 && (
                                        <PhoneNumbers
                                          primary={contact.primary}
                                          phones={contact.phones}
                                        />
                                      )}
                                    <NoteLink href={`mailto:${contact.email}`}>
                                      <Abbr title={contact.email}>{contact.email}</Abbr>
                                    </NoteLink>
                                  </ContactWrapper>
                                ))}
                              </Inline>
                            </CompanyWrapper>
                          ))}
                        </CompanyWrapper>
                      );
                    })}
                  </Inline>
                ))}
              </Notes>
            </Detail>
          ))}
        </ContactsMasonry>
      ) : (
        <NotFound>
          {roleCanAccessResource("facility_information", "update") &&
            !facility.isDeleted &&
            contacts && (
              <Edit type="button" onClick={() => setShowEditContacts(true)}>
                <FontAwesomeIcon icon={faEdit} />
              </Edit>
            )}
          {contacts && Object.keys(contacts).length === 0 ? (
            <NoContacts>
              <Text>No Contacts</Text>
            </NoContacts>
          ) : (
            <NotLoaded>
              <Loader />
            </NotLoaded>
          )}
        </NotFound>
      )}
      {showEditContacts && (
        <ModalEditContacts
          visible={showEditContacts}
          setVisible={setShowEditContacts}
          facility={facility}
          setFacility={setFacility}
          setRefreshFacility={() => setFacility(null)}
        />
      )}
    </>
  );
};

ContactsPage.propTypes = {
  facility: PropTypes.objectOf(PropTypes.any).isRequired
};

// Style Overrides
const NoContacts = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin: ${pad}px 0;
`;

const Detail = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin: ${pad}px 0;

  &:last-child {
    margin-bottom: 0;
  }

  p {
    color: ${({theme}) => theme.secondary};
  }

  ${({fullWidth}) =>
    fullWidth &&
    css`
      width: 100%;
      flex-direction: column;
    `}
`;

const DetailLabel = styled(Text)`
  width: 40%;
  padding-right: ${pad}px;
  text-overflow: ellipsis;
  overflow: hidden;
  font-size: 19px;
  margin-bottom: ${pad}px;

  ${({fullWidth}) =>
    fullWidth &&
    css`
      width: 100%;
      padding: 0;
      overflow: inherit;
    `}
`;

const Notes = styled.div`
  display: block;
  width: 60%;
  height: 100%;

  ${({fullWidth}) =>
    fullWidth &&
    css`
      width: 100%;
    `}
`;

const NoteText = styled(Text)`
  display: block;
  color: ${({theme}) => theme.secondary};
  word-wrap: ${({wrap}) => (wrap ? "break-word" : "normal")};
  text-overflow: ${({wrap}) => (wrap ? "unset" : "ellipsis")};
  white-space: ${({wrap}) => (wrap ? "unset" : "nowrap")};
  overflow: hidden;
`;

const NoteLink = styled(AnchorInline)`
  ${voice.quiet};
  display: block;
  color: ${({theme}) => theme.primary};
  word-wrap: ${({wrap}) => (wrap ? "break-word" : "normal")};
  text-overflow: ${({wrap}) => (wrap ? "unset" : "ellipsis")};
  white-space: ${({wrap}) => (wrap ? "unset" : "nowrap")};
  overflow: hidden;
  margin-bottom: ${pad / 2}px;
  position: relative;
  z-index: ${z("above")};
`;

const Edit = styled.button`
  position: absolute;
  top: ${pad / 2}px;
  right: ${pad}px;
  width: fit-content;
  z-index: ${z("base") + 1};

  svg {
    fill: ${({theme}) => theme.secondary};
    padding: ${pad / 2}px 0 ${pad}px ${pad}px;
  }
`;

const ContactWrapper = styled.div`
  position: relative;
  padding: ${pad}px ${pad * 4}px ${pad * 2}px 0;
  width: ${({numItems, totalItems}) => (totalItems ? `${(numItems * 100) / totalItems}%` : "100%")};
  height: 80px;
`;

const CompanyWrapper = styled.div`
  position: relative;
  width: ${({numItems, totalItems}) => (totalItems ? `${(numItems * 100) / totalItems}%` : "100%")};
`;

const ContactsMasonry = styled(Masonry)`
  padding: 0 ${pad * 2}px;
`;

const NotFound = styled(MasonryItem)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 75px;
  padding: ${pad}px;
  border-radius: ${radius};
  border: ${border} solid ${({theme}) => theme.secondary};
  max-width: 100% !important;
  flex: unset;
`;

export default ContactsPage;
